
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import {
  updatePlacementFees,
  updateTable,
  uploadOfficeDocumentToS3,
  setFieldToNULL,
  createNote,
  removeLocationDocument
} from "@/api";
import UpdateMultipleItems from "../components/UpdateMultipleItems";
import ColumnManager from "@/components/shared/ColumnManager.vue";
import TruncatedTextCell from "@/components/shared/TruncatedTextCell";
import { headersInterface } from "@/interfaces/Tables.interfaces";
import { startCase } from "lodash";
import { constants } from "@/utils/constants";
import { getDataTables } from "@/mixins/getDataTables";
import { formatNotes, getHeaders } from "@/helpers";
import { formatCentralBooking } from "@/helpers/adminEnumLabels";
import { format, parseISO, isValid as isValidDate } from "date-fns";

export default Vue.extend({
  name: "Location",
  components: {
    UpdateMultipleItems,
    ColumnManager,
    TruncatedTextCell
  },
  mixins: [getDataTables],
  props: {
    /** When true, renders a client-side table from `embeddedItems` (no server fetch). */
    embedded: {
      type: Boolean,
      default: false
    },
    embeddedItems: {
      type: Array,
      default: () => []
    },
    embeddedLoading: {
      type: Boolean,
      default: false
    }
  },
  data: () => {
    return {
      tableSearch: "",
      options: {} as any,
      searchQuery: "",
      managedHeaders: [] as any[],
      baseLocationHeaders: constants.enabledColumns.locationTable.map(
        (col: string) => ({
          text: startCase(col),
          value: col,
          align: "left"
        })
      ),
      freePlacements: {
        email: "",
        loginEmail: "",
        placementFees: "",
        id: 0,
        practiceName: ""
      },
      freePlacementsDialog: false,
      freePlacementsSaving: false,
      freePlacementsNote: "",
      currentFreePlacements: 0,
      currentTokenAmount: 0,
      currentTokenItem: null as any,
      tokenNote: "",
      countryCode: constants.countryCode,
      // Office Document Management
      addDocumentDialog: false,
      removeDocumentDialog: false,
      documentUrl: "",
      documentLoading: false,
      selectedFile: null as File | null,
      fileError: "",
      selectedLocation: {
        id: 0,
        email: "",
        practiceName: "",
        officeDocument: ""
      },
      // Stripe Customer ID Removal
      showModal: false,
      modalHeader: "",
      action: () => {},
      currentItem: { id: 0 },
      previousStripeCustomerId: null as string | null,
      // Deactivation toggle
      deactivationItem: null as any
    };
  },
  computed: {
    ...mapState({
      tablename: "tablename",
      headers: "tableHeaders",
      content: "tableContent"
    }),
    tableHeaders(): Array<headersInterface> {
      // Use managed headers if available (from ColumnManager)
      if (
        (this as any).managedHeaders &&
        (this as any).managedHeaders.length > 0
      ) {
        return (this as any).managedHeaders as Array<headersInterface>;
      }

      // Default headers
      const tableColumns = constants.enabledColumns.locationTable;
      const data = tableColumns.map(i => {
        let headerText = startCase(i);
        if (i === "deactivated") {
          headerText = "Deleted";
        } else if (i === "email") {
          headerText = "Notification Email";
        } else if (i === "loginEmail") {
          headerText = "Login Email";
        }
        return { align: "left", text: headerText, value: i };
      });
      return data;
    },
    dataTableLengthBinding(): Record<string, number> {
      if (this.embedded) {
        return {};
      }
      return { "server-items-length": this.totalItems };
    },
    sourceRows(): Array<any> {
      if (this.embedded) {
        return this.embeddedItems || [];
      }
      return this.content || [];
    },
    formattedContent(): Array<any> {
      if (!this.sourceRows || this.sourceRows.length === 0) {
        return [];
      }
      // Get all required fields from table headers
      const requiredFields = this.tableHeaders.map(header => header.value);

      return this.sourceRows.map(item => {
        const formatted = { ...item };

        // Ensure all required fields exist, initialize missing ones with null
        requiredFields.forEach(field => {
          if (!(field in formatted)) {
            formatted[field] = null;
          }
        });

        // Format fields that don't have custom templates
        const customTemplateFields = [
          "loginEmail",
          "email",
          "secondaryEmail",
          "practiceName",
          "primaryContact",
          "city",
          "province",
          "country",
          "postalCode",
          "officeDocument",
          "isReliable",
          "accountingEmail",
          "phone",
          "address",
          "notes",
          "isCentralBooked",
          "deactivated",
          "placementFees",
          "defaultProposedRateHygienist",
          "defaultProposedRateAssistant",
          "stripeCustomerId",
          "revenueStripeCustomerId",
          "tokenAmount",
          "lat",
          "lon",
          "regionId",
          "photoUrl",
          "createdOn"
        ];

        Object.keys(formatted).forEach(key => {
          const value = formatted[key];

          // Skip custom template fields - they handle their own formatting
          if (customTemplateFields.includes(key)) {
            // Ensure custom template fields have a value (even if null/undefined)
            // The templates will handle the display logic
            if (value === undefined) {
              formatted[key] = null;
            }
            return;
          }

          // Format non-custom template fields
          if (
            value === null ||
            value === undefined ||
            value === "" ||
            (typeof value === "string" && value.trim() === "")
          ) {
            formatted[key] = "-";
          }
        });

        return formatted;
      });
    }
  },
  watch: {
    content: {
      handler() {},
      immediate: true
    },
    searchQuery() {
      (this as any).handleFilterChange();
    }
  },
  created() {
    // T2-6225: when navigated to via an EntityIdLink (?locationId=N), seed the
    // search so the table filters straight to that location.
    const linkedLocationId = (this as any).$route?.query?.locationId;
    if (linkedLocationId) {
      (this as any).searchQuery = String(linkedLocationId);
    }
    if (this.embedded) {
      this.options = {
        page: 1,
        itemsPerPage: 15,
        sortBy: ["id"],
        sortDesc: [true]
      };
      return;
    }
    // Set the tablename in the Vuex store so getDataTables mixin can fetch data
    this.$store.commit("modify", { property: "tablename", value: "Location" });
  },
  mounted() {
    if (this.embedded) {
      return;
    }
    if (this.options && this.options.page && this.options.itemsPerPage) {
      (this as any).getDataFromApi();
    }
  },
  methods: {
    ...mapMutations(["modify", "modifyMultiple", "callSnackbar"]),
    formatCentralBooking,
    // T2-6225: modifier / middle clicks fall through to the native anchor
    // (new tab); a plain click is prevented so the row click selects instead.
    onIdChipClick(event: MouseEvent) {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.button !== 0
      ) {
        event.stopPropagation();
        return;
      }
      event.preventDefault();
    },
    async getDataFromApi() {
      const vm = this as any;

      if (vm.embedded) {
        return;
      }

      if (vm.loading) {
        return;
      }

      vm.loading = true;
      try {
        const itemsPerPage = vm.options.itemsPerPage || 15;
        const requestedPage = vm.options.page || 1;
        const { sortBy = [], sortDesc = [] } = vm.options;

        // Load only requested page, not all data
        const columnKey = vm.tablename.toLowerCase() + "Table";
        const columnNames =
          constants.enabledColumns[columnKey] ||
          constants.enabledColumns.locationTable;

        let searchString: string = "";
        let searchColumns: string[] = [];

        const query = vm.searchQuery?.trim() || "";

        if (query.length > 0) {
          const strippedQuery = query.replace(/[\s\-()+ ]/g, "");
          const queryAsNumber = parseInt(strippedQuery);
          const isPurelyNumeric = !isNaN(queryAsNumber) && strippedQuery === queryAsNumber.toString();

          if (isPurelyNumeric && strippedQuery.length >= 7) {
            // Phone number search (7+ digits) — also include ID
            searchString = strippedQuery;
            searchColumns = ["id", "phone"];
          } else if (isPurelyNumeric) {
            // Short number — ID exact match only
            searchString = strippedQuery;
            searchColumns = ["id"];
          } else if (query.length >= 3) {
            // Text search - search across all relevant fields
            searchString = query;
            searchColumns = [
              "practiceName",
              "primaryContact",
              "email",
              "loginEmail", // T2-6223: include the User login email in search
              "phone"
            ];
          }
        }

        // Use getPaginateData for server-side pagination with search
        await vm.$store.dispatch("getPaginateData", {
          tableName: vm.tablename,
          limit: itemsPerPage,
          page: requestedPage,
          columnNames,
          ...(searchString.length > 0 && {
            search: searchString,
            searchColumns: searchColumns
          })
        });

        // Data already filtered and paginated by server
        const pageItems = [...(vm.$store.state.tableContent || [])];
        const headers = pageItems.length > 0 ? getHeaders(pageItems) : [];

        // Get total count from server
        if (vm.totalItems === 0 || searchString.length > 0) {
          const totalCountParams = {
            tableName: vm.tablename,
            columnNames,
            ...(searchString.length > 0 && {
              search: searchString,
              searchColumns: searchColumns
            })
          };
          const { totalItemsByTable } = await import("@/api");
          vm.totalItems = await totalItemsByTable(totalCountParams);
        }

        (this as any).modifyMultiple([
          { property: "tableHeaders", value: headers },
          { property: "tableContent", value: pageItems }
        ]);
      } catch (error) {
        vm.callSnackbar({
          visible: true,
          text: "Error loading locations",
          color: "red darken-2"
        });
      } finally {
        vm.loading = false;
      }
    },
    handleFilterChange() {
      const vm = this as any;
      if (vm.loading) {
        return;
      }
      // Reset total count when searching to force server refresh
      vm.totalItems = 0;

      if (!vm.options) {
        this.getDataFromApi();
        return;
      }
      if (!vm.options.page || vm.options.page === 1) {
        this.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    handleSearchEnter() {
      // Trigger search immediately when Enter is pressed
      const vm = this as any;
      if (vm.loading) {
        return;
      }
      // Reset total count when searching to force server refresh
      vm.totalItems = 0;

      // Ensure we're on page 1 when searching
      if (vm.options) {
        vm.options.page = 1;
      }

      // Trigger the search
      this.getDataFromApi();
    },
    findHeaderIndex(name: string): number {
      return this.headers.findIndex(item => item.value === name);
    },
    formatNotes(note: string | null) {
      return formatNotes(note);
    },
    formatDateField(value: any): string {
      if (
        !value ||
        value === null ||
        value === "" ||
        value === "0000-00-00" ||
        value === "0000-00-00 00:00:00"
      ) {
        return "-";
      }
      try {
        const parsed =
          typeof value === "string" ? parseISO(value) : new Date(value);
        if (isValidDate(parsed)) {
          return format(parsed, "MMM d, yyyy HH:mm");
        }
        return value.toString();
      } catch (error) {
        return value ? value.toString() : "-";
      }
    },
    savePlacementFees(item) {
      this.freePlacements = item;
      this.freePlacementsNote = "";
      this.freePlacementsDialog = true;
    },
    cancelFreePlacements() {
      this.freePlacements.placementFees = String(this.currentFreePlacements);
      this.freePlacementsDialog = false;
      this.freePlacementsNote = "";
    },
    async freePlacementsSetNewValue() {
      if (this.freePlacementsSaving) return;
      const rawBalance = this.freePlacements.placementFees;
      const balance = Number(rawBalance);
      if (
        rawBalance === "" ||
        rawBalance == null ||
        !Number.isInteger(balance) ||
        balance < 0 ||
        balance > 2147483647
      ) {
        (this as any).callSnackbar({
          visible: true,
          text: "Placement credit balance must be a non-negative whole number.",
          color: "red darken-2"
        });
        return;
      }
      this.freePlacementsSaving = true;
      try {
        const userString = localStorage.getItem("user") || "";
        const agent = userString.startsWith("{")
          ? JSON.parse(userString)
          : userString;

        // Get agent name string for source
        let agentName = "Admin";
        if (agent) {
          agentName =
            typeof agent === "string"
              ? agent
              : agent.username || agent.email || "Admin";
        }

        await updatePlacementFees({
          email: this.freePlacements.loginEmail,
          numGiven: balance,
          addNumGiven:
            parseInt(this.freePlacements.placementFees) -
            this.currentFreePlacements,
          locationId: this.freePlacements.id,
          practiceName: this.freePlacements.practiceName,
          agent: agent,
          note: this.freePlacementsNote || ""
        });

        // Also save to AdminNotes if note provided
        if (this.freePlacementsNote && this.freePlacementsNote.trim()) {
          try {
            await createNote({
              entityId: this.freePlacements.id,
              type: "Location",
              note: `(Free Placements updated to ${
                this.freePlacements.placementFees
              }) - ${this.freePlacementsNote.trim()}`,
              source: agentName
            });
          } catch (noteError) {
            console.error(
              "Error saving free placement note to AdminNotes:",
              noteError
            );
          }
        }

        this.freePlacementsDialog = false;
        this.freePlacementsNote = "";
        this.currentFreePlacements = balance;
        (this as any).callSnackbar({
          visible: true,
          text: "Free placements and note saved",
          color: "green darken-2"
        });
      } catch (error) {
        this.freePlacements.placementFees = String(this.currentFreePlacements);
        this.freePlacementsDialog = false;
        (this as any).callSnackbar({
          visible: true,
          text:
            error instanceof Error
              ? error.message
              : "Could not confirm the credit update. Refresh the office before trying again.",
          color: "red darken-2"
        });
      } finally {
        this.freePlacementsSaving = false;
      }
    },
    openTokenDialog(item) {
      this.currentTokenAmount = item.tokenAmount;
      this.currentTokenItem = item;
      this.tokenNote = "";
    },
    async saveTokenAmount(item) {
      try {
        await updateTable({
          tablename: "User",
          columnname: "TokenAmount",
          id: item.userID,
          value: item.tokenAmount,
          prevValue: this.currentTokenAmount
        });

        // Add note to AdminNotes if provided
        if (this.tokenNote && this.tokenNote.trim() && item.id) {
          try {
            // Get current user info for source
            const userString = localStorage.getItem("user") || "";
            let agent = "Admin";
            if (userString) {
              try {
                const userObj = userString.startsWith("{")
                  ? JSON.parse(userString)
                  : userString;
                agent =
                  typeof userObj === "string"
                    ? userObj
                    : userObj.username || userObj.email || "Admin";
              } catch {
                agent = userString;
              }
            }

            await createNote({
              entityId: item.id,
              type: "Location",
              note: `(Tokens updated to ${
                item.tokenAmount
              }) - ${this.tokenNote.trim()}`,
              source: agent
            });
          } catch (noteError) {
            console.error("Error saving token note:", noteError);
          }
        }

        this.tokenNote = "";
        (this as any).callSnackbar({
          visible: true,
          text: "Token amount and note saved",
          color: "green darken-2"
        });
      } catch (error) {
        (this as any).callSnackbar({
          visible: true,
          text: "Error saving data",
          color: "red darken-2"
        });
      }
    },

    async saveStripeCustomerId(item) {
      try {
        await updateTable({
          tablename: this.tablename,
          columnname: "stripeCustomerId",
          id: item.id,
          value: item.stripeCustomerId || null,
          prevValue: this.previousStripeCustomerId
        });

        // Refresh the table data
        await (this as any).getDataFromApi(true);

        (this as any).callSnackbar({
          visible: true,
          text: "Stripe Customer ID saved successfully",
          color: "green darken-2"
        });
      } catch (error) {
        (this as any).callSnackbar({
          visible: true,
          text: "Error saving Stripe Customer ID",
          color: "red darken-2"
        });
      }
    },

    // Office Document Management Methods
    openAddDocumentDialog(item: any) {
      this.selectedLocation = {
        id: item.id,
        email: item.email,
        practiceName: item.practiceName,
        officeDocument: item.officeDocument || ""
      };
      this.documentUrl = "";
      this.selectedFile = null;
      this.fileError = "";
      this.addDocumentDialog = true;
    },

    closeAddDocumentDialog() {
      this.addDocumentDialog = false;
      this.documentUrl = "";
      this.selectedFile = null;
      this.fileError = "";
      this.selectedLocation = {
        id: 0,
        email: "",
        practiceName: "",
        officeDocument: ""
      };
    },

    openFileSelector() {
      const fileInput = this.$refs.fileInput as HTMLInputElement;
      if (fileInput) {
        fileInput.click();
      }
    },

    handleFileSelect(event: Event) {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (!file) {
        return;
      }

      // Reset error
      this.fileError = "";

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/rtf",
        "text/richtext",
        "text/plain"
      ];

      if (!allowedTypes.includes(file.type)) {
        this.fileError =
          "Invalid file type. Please select a PDF, Word, RTF, or Text document.";
        this.selectedFile = null;
        return;
      }

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        this.fileError =
          "File size exceeds 10MB limit. Please select a smaller file.";
        this.selectedFile = null;
        return;
      }

      // File is valid
      this.selectedFile = file;
    },

    clearSelectedFile() {
      this.selectedFile = null;
      this.fileError = "";
      // Reset file input
      if (this.$refs.fileInput) {
        (this.$refs.fileInput as HTMLInputElement).value = "";
      }
    },

    formatFileSize(bytes: number): string {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },

    openRemoveDocumentDialog(item: any) {
      this.selectedLocation = {
        id: item.id,
        email: item.email,
        practiceName: item.practiceName,
        officeDocument: item.officeDocument || ""
      };
      this.removeDocumentDialog = true;
    },

    closeRemoveDocumentDialog() {
      this.removeDocumentDialog = false;
      this.selectedLocation = {
        id: 0,
        email: "",
        practiceName: "",
        officeDocument: ""
      };
    },

    isValidUrl(url: string): boolean {
      if (!url) return false;
      try {
        const urlObj = new URL(url);
        return urlObj.protocol === "http:" || urlObj.protocol === "https:";
      } catch {
        return false;
      }
    },

    async addDocument() {
      if (!this.selectedFile) {
        (this as any).callSnackbar({
          visible: true,
          text: "Please select a file to upload",
          color: "red darken-2"
        });
        return;
      }

      this.documentLoading = true;
      try {
        // Upload file to S3 and get the URL
        const documentUrl = await uploadOfficeDocumentToS3(this.selectedFile);

        // Update location with the document URL
        await updateTable({
          tablename: "Location",
          columnname: "photoUrl",
          id: this.selectedLocation.id,
          value: documentUrl,
          prevValue: this.selectedLocation.officeDocument || null
        });

        // Refresh the table data to show the new document
        await (this as any).getDataFromApi(true);

        (this as any).callSnackbar({
          visible: true,
          text: "Office document uploaded successfully",
          color: "green darken-2"
        });

        this.closeAddDocumentDialog();
      } catch (error) {
        (this as any).callSnackbar({
          visible: true,
          text: "Error uploading document. Please try again.",
          color: "red darken-2"
        });
      } finally {
        this.documentLoading = false;
      }
    },

    async removeDocument() {
      this.documentLoading = true;
      try {
        await removeLocationDocument(this.selectedLocation.id);

        // Refresh the table data to show the document removal
        await (this as any).getDataFromApi(true);

        (this as any).callSnackbar({
          visible: true,
          text: "Office document removed successfully",
          color: "green darken-2"
        });

        this.closeRemoveDocumentDialog();
      } catch (error) {
        (this as any).callSnackbar({
          visible: true,
          text: "Error removing document",
          color: "red darken-2"
        });
      } finally {
        this.documentLoading = false;
      }
    },

    // Stripe Customer ID Removal Methods
    async modalSubmit() {
      this.action();
      this.modalHeader = "";
      this.showModal = false;
    },

    clickSetStripeCustomerIdToNull() {
      this.modalHeader = "Set this Stripe Customer ID to NULL?";
      this.showModal = true;
      this.action = this.setStripeCustomerIdToNull;
    },

    async setStripeCustomerIdToNull() {
      try {
        await setFieldToNULL({
          tablename: this.tablename,
          columnname: "stripeCustomerId",
          id: this.currentItem.id
        });

        // Refresh the table data
        await (this as any).getDataFromApi(true);

        (this as any).callSnackbar({
          visible: true,
          text: "Stripe Customer ID removed successfully",
          color: "green darken-2"
        });
      } catch (error) {
        (this as any).callSnackbar({
          visible: true,
          text: "Error removing Stripe Customer ID",
          color: "red darken-2"
        });
      }
    },

    // Deactivation Toggle Methods
    toggleDeactivated(item: any) {
      this.deactivationItem = item;
      const newStatus = item.deactivated === 1 ? 0 : 1;
      const actionText = item.deactivated === 1 ? "REACTIVATE" : "DEACTIVATE";

      this.modalHeader = `Are you sure you want to ${actionText} ${item.practiceName}?`;
      this.showModal = true;
      this.action = () => this.confirmToggleDeactivated(newStatus);
    },

    async confirmToggleDeactivated(newStatus: number) {
      if (!this.deactivationItem) return;

      try {
        await updateTable({
          tablename: this.tablename,
          columnname: "deactivated",
          id: this.deactivationItem.id,
          value: newStatus,
          prevValue: this.deactivationItem.deactivated
        });

        // Refresh the table data
        await (this as any).getDataFromApi(true);

        const actionText = newStatus === 0 ? "reactivated" : "deactivated";
        (this as any).callSnackbar({
          visible: true,
          text: `Location ${actionText} successfully`,
          color: "green darken-2"
        });

        this.deactivationItem = null;
      } catch (error) {
        (this as any).callSnackbar({
          visible: true,
          text: "Error updating deactivation status",
          color: "red darken-2"
        });
      }
    },

    // Row Selection Methods (for Master-Detail View)
    handleRowClick(item: any) {
      // Emit event for parent component to listen to
      this.$emit("location-selected", item);
      // Also update store for global access
      this.$store.commit("modify", {
        property: "selectedLocationId",
        value: item.id
      });
    },

    getRowClass(item: any) {
      const selectedId = this.$store.state.selectedLocationId;
      let classes = selectedId === item.id ? "selected-location-row" : "";
      if (item.deactivated === 1) {
        classes += " deactivated-row";
      }
      return classes.trim();
    },
    updateLocationHeaders(headers: any[]) {
      (this as any).managedHeaders = headers;
    },
    handleColumnSnackbar(options: { text: string; color: string }) {
      (this as any).callSnackbar({
        visible: true,
        text: options.text,
        color: options.color
      });
    }
  }
});
